import React, { Fragment, useState, useMemo } from 'react';
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import JsonLd, { webAppSchema } from 'shared/JsonLd';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import ToolLeadCapture from '../../components/ToolLeadCapture/ToolLeadCapture';

const OCCUPANCY = [
    { v: 'r2', label: 'Multifamily (Group R-2)' },
    { v: 'mixed', label: 'Mixed-use' },
    { v: 'commercial', label: 'Commercial / Office' },
    { v: 'assembly', label: 'Assembly / Institutional / Other' },
    { v: 'r3', label: 'Single-family, duplex, or townhome (Group R-3)' },
];

const clampInt = (val, min, max) => {
    const n = parseInt(val, 10);
    if (Number.isNaN(n)) return min;
    return Math.min(max, Math.max(min, n));
};

// Local styles for the occupancy picker (mirrors the brand pill/segmented
// pattern used elsewhere on the site, kept under its own "aor__" namespace
// so nothing collides with other pages' component CSS).
const AOR_CSS = `
.aor__pills{display:flex;flex-wrap:wrap;gap:9px}
.aor__pill{background:#F7FAFD;border:1.5px solid #D7E2F1;border-radius:999px;padding:10px 16px;cursor:pointer;font-family:'Axiforma SemiBold',sans-serif;color:#16203C;font-size:13.5px;line-height:1.3;transition:border-color .15s,background-color .15s,color .15s,box-shadow .15s}
.aor__pill:hover:not([aria-pressed=true]){border-color:#B9CBEA;background:#fff}
.aor__pill[aria-pressed=true]{border-color:#0044EB;background:#0044EB;color:#fff}
.aor__pill:focus-visible{outline:none;box-shadow:0 0 0 4px rgba(0,68,235,.18)}
`;

// Joined pill group for the (short, 5-option) occupancy choice — replaces the
// native <select>. Single-select: exactly one pill is pressed at a time.
const OccupancyPicker = ({ options, value, onChange, ariaLabel }) => (
    <div className="aor__pills" role="group" aria-label={ariaLabel}>
        {options.map((o) => (
            <button
                type="button"
                key={o.v}
                className="aor__pill"
                aria-pressed={value === o.v}
                onClick={() => onChange(o.v)}
            >
                {o.label}
            </button>
        ))}
    </div>
);

const AreaOfRefugeCheckerPage = () => {
    usePageSeo({
        title: 'Area of Refuge Requirements Checker — CBC 1009',
        description: 'Free area of refuge requirements checker. See if your building needs areas of refuge and two-way communication at elevator landings under CBC 1009.',
        keywords: 'area of refuge required, two-way communication system, CBC 1009, accessible means of egress, elevator landing communication, area of rescue assistance, ADA refuge area, BUILDLAB fire life safety',
    });

    const [occupancy, setOccupancy] = useState('r2');
    const [floors, setFloors] = useState(4);
    const [cabs, setCabs] = useState(1);
    const [sprinklered, setSprinklered] = useState(true);

    const result = useMemo(() => {
        // Group R-3: detached dwellings, duplexes, townhomes — not subject to the
        // accessible means of egress / area of refuge provisions of CBC §1009.
        if (occupancy === 'r3') {
            return {
                headline: 'Likely not applicable',
                tone: 'exempt',
                areaOfRefuge: 'Not applicable',
                twoWay: 'Not applicable',
                stations: 0,
                elevatorReq: false,
                reason: 'Group R-3 occupancies (single-family homes, duplexes, and townhomes) are generally outside the scope of the accessible means of egress provisions in CBC §1009.',
            };
        }

        // Single story at the level of exit discharge — no story above/below it,
        // so areas of refuge and elevator-landing communication are not triggered.
        if (floors <= 1) {
            return {
                headline: 'Likely not required',
                tone: 'exempt',
                areaOfRefuge: 'Not required',
                twoWay: 'Not required',
                stations: 0,
                elevatorReq: false,
                reason: 'A single-story building sits at the level of exit discharge with no stories above or below it, so areas of refuge and elevator-landing two-way communication are generally not triggered under CBC §1009.',
            };
        }

        const accessibleFloors = Math.max(0, floors - 1); // stories above/below the level of exit discharge
        const elevatorReq = floors >= 4; // §1009.2.1 — an elevator must serve as an accessible MOE in buildings 4+ stories
        // Two-way communication is required at the landing serving each elevator (or bank)
        // on every accessible floor above/below the level of exit discharge (§1009.8).
        const stations = cabs > 0 ? cabs * accessibleFloors : 0;

        if (sprinklered) {
            return {
                headline: cabs > 0 ? 'Two-way communication required' : 'Areas of refuge not required',
                tone: cabs > 0 ? 'required' : 'exempt',
                areaOfRefuge: 'Not required (sprinklered)',
                twoWay: cabs > 0 ? 'Required' : 'Not required',
                stations,
                elevatorReq,
                reason: 'Because the building is sprinklered throughout (NFPA 13 / 13R), areas of refuge are NOT required at the exit stairways (CBC §1009.3 Exception 1) and elevators need not be reached from an area of refuge (§1009.4 Exception 1). However, a two-way communication system is still required at each elevator landing on every floor above or below the level of exit discharge (§1009.8).',
            };
        }

        return {
            headline: 'Areas of refuge required',
            tone: 'required',
            areaOfRefuge: 'Required at each exit stairway',
            twoWay: cabs > 0 ? 'Required' : 'Required at each area of refuge',
            stations,
            elevatorReq,
            reason: 'The building is multi-story and not sprinklered throughout, so each required exit stairway must incorporate or be reached from an area of refuge (CBC §1009.3). Two-way communication is required both within each area of refuge (§1009.6.5) and at the elevator landings (§1009.8).',
        };
    }, [occupancy, floors, cabs, sprinklered]);

    const exempt = result.tone === 'exempt';

    return (
        <Fragment>
            <style dangerouslySetInnerHTML={{ __html: AOR_CSS }} />
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={'Area of Refuge Requirements Checker'} pagesub={'CBC 1009 · Two-Way Communication'} pageTop={'Fire & Life Safety'} />
                <JsonLd data={webAppSchema({ name: 'Area of Refuge Requirements Checker', description: 'Free checker for CBC 1009 area-of-refuge and two-way communication requirements.', path: '/area-of-refuge-checker' })} />
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="row mb-4">
                            <div className="col-lg-9">
                                <h2 className="mb-3">Does your building need areas of refuge or two-way emergency communication?</h2>
                                <p>
                                    California Building Code <strong>§1009</strong> governs accessible means of egress. Multi-story
                                    buildings frequently need <strong>areas of refuge</strong> and <strong>two-way communication
                                    systems</strong> at elevator landings so that people who cannot use the stairs can signal
                                    responders during an emergency. Tell us about the building below to see what's likely required —
                                    including a quick estimate of how many communication stations you'll need.
                                </p>
                            </div>
                        </div>

                        <div className="row">
                            {/* Inputs */}
                            <div className="col-lg-7 mb-4">
                                <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 16, padding: '24px 28px' }}>
                                    <div className="mb-3">
                                        <label className="input_title d-block mb-1">Occupancy / building type</label>
                                        <OccupancyPicker options={OCCUPANCY} value={occupancy} onChange={setOccupancy} ariaLabel="Occupancy type" />
                                    </div>
                                    <div className="row">
                                        <div className="col-sm-6 mb-3">
                                            <label className="input_title d-block mb-1">Number of floors (stories)</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                min="1"
                                                max="80"
                                                value={floors}
                                                onChange={(e) => setFloors(clampInt(e.target.value, 1, 80))}
                                                aria-label="Number of floors"
                                            />
                                        </div>
                                        <div className="col-sm-6 mb-3">
                                            <label className="input_title d-block mb-1">Number of elevator cabs</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                min="0"
                                                max="24"
                                                value={cabs}
                                                onChange={(e) => setCabs(clampInt(e.target.value, 0, 24))}
                                                aria-label="Number of elevator cabs"
                                            />
                                        </div>
                                    </div>
                                    <label style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer', fontSize: '.92rem' }}>
                                        <input type="checkbox" checked={sprinklered} onChange={() => setSprinklered((v) => !v)} />
                                        Building is <strong>sprinklered throughout</strong> (NFPA 13 / 13R)
                                    </label>
                                </div>
                            </div>

                            {/* Verdict */}
                            <div className="col-lg-5 mb-4">
                                <div style={{ background: 'var(--bs-dark)', borderRadius: 16, padding: '28px 30px', color: '#fff', height: '100%' }}>
                                    <div style={{ fontSize: '.85rem', opacity: 0.8 }}>CBC §1009 assessment</div>
                                    <div style={{ fontSize: '1.7rem', fontWeight: 800, lineHeight: 1.15, margin: '4px 0 14px', color: exempt ? '#7CFFB2' : '#FFC9A3' }}>
                                        {result.headline}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px 12px', fontSize: '.9rem', marginBottom: 14 }}>
                                        <span style={{ opacity: 0.8 }}>Areas of refuge</span>
                                        <strong style={{ textAlign: 'right' }}>{result.areaOfRefuge}</strong>
                                        <span style={{ opacity: 0.8 }}>Two-way communication</span>
                                        <strong style={{ textAlign: 'right' }}>{result.twoWay}</strong>
                                        {result.stations > 0 && (
                                            <>
                                                <span style={{ opacity: 0.8 }}>Est. comm. stations</span>
                                                <strong style={{ textAlign: 'right' }}>~{result.stations}</strong>
                                            </>
                                        )}
                                        {result.elevatorReq && (
                                            <>
                                                <span style={{ opacity: 0.8 }}>Egress elevator</span>
                                                <strong style={{ textAlign: 'right' }}>Required</strong>
                                            </>
                                        )}
                                    </div>
                                    <p style={{ fontSize: '.88rem', opacity: 0.92 }}>{result.reason}</p>
                                    {result.stations > 0 && (
                                        <p style={{ fontSize: '.82rem', opacity: 0.78 }}>
                                            Station estimate = {cabs} {cabs === 1 ? 'cab' : 'cabs'} × {Math.max(0, floors - 1)} floor(s)
                                            above/below the level of exit discharge. Elevators that share a common landing (a bank)
                                            may share a single station, which can reduce this count.
                                        </p>
                                    )}
                                    <Link to="/contact" className="btn btn-primary w-100 mt-3" style={{ display: 'block', textAlign: 'center' }}>
                                        <span className="btn_label" data-text="Plan my life-safety system">Plan my life-safety system</span>
                                    </Link>
                                    <ToolLeadCapture
                                        tool="Area of Refuge Checker"
                                        summary={`${result.headline} — areas of refuge: ${result.areaOfRefuge}; two-way comms: ${result.twoWay}${result.stations > 0 ? '; ~' + result.stations + ' stations' : ''} (${floors} floors, ${cabs} cabs, ${sprinklered ? 'sprinklered' : 'not sprinklered'})`}
                                    />
                                </div>
                            </div>
                        </div>

                        <p className="small text-muted mt-3" style={{ maxWidth: 880 }}>
                            Source: California Building Code §1009, Accessible Means of Egress (areas of refuge §1009.3 / §1009.6,
                            elevators §1009.2.1 / §1009.4, two-way communication §1009.8), based on the 2022 CBC. This is a planning
                            guide only — the final determination depends on your full egress design and is made by your local
                            building official / AHJ, which may adopt amendments. BUILDLAB designs and installs two-way emergency
                            communication and area-of-refuge systems in-house.
                        </p>

                        <p className="small mt-3" style={{ maxWidth: 880 }}>
                            Also verify emergency responder radio coverage with the{' '}
                            <Link to="/das-erccs-checker">DAS / ERRCS requirement checker</Link>, or see our{' '}
                            <Link to="/service-single/Fire-and-Life-Safety">fire &amp; life safety design services</Link>.
                        </p>
                    </div>
                </section>
                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default AreaOfRefugeCheckerPage;
